import { useState, useEffect, useCallback, useRef } from 'react'
import { getBaseUrl } from '@/lib/url'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { apiRequest } from '@/lib/supabase'
import { useToast } from '@/hooks/useToast'
import { useTagsQuery } from '@/hooks/useTags'

interface CreateLinkModalProps {
  isOpen: boolean
  onClose: () => void
}

interface CreateLinkResponse {
  shortUrl: string
  shortcode: string
  original_url: string
  id: string
}

const QUICK_TAG_COLORS = ['#22c55e', '#eab308', '#3b82f6', '#ec4899', '#8b5cf6']

export function CreateLinkModal({ isOpen, onClose }: CreateLinkModalProps) {
  const [url, setUrl] = useState('')
  const [alias, setAlias] = useState('')
  const [shortUrl, setShortUrl] = useState('')
  const [originalUrl, setOriginalUrl] = useState('')
  const [showSuccess, setShowSuccess] = useState(false)
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([])
  const [showNewTag, setShowNewTag] = useState(false)
  const [newTagName, setNewTagName] = useState('')
  const [newTagColor, setNewTagColor] = useState(QUICK_TAG_COLORS[0])
  const { showToast } = useToast()
  const queryClient = useQueryClient()
  const { data: allTags = [] } = useTagsQuery()

  // Create tag mutation
  const createTagMutation = useMutation({
    mutationFn: () => apiRequest<{ id: string; name: string; color: string }>('/api/tags', 'POST', { name: newTagName, color: newTagColor }),
    onSuccess: (data) => {
      setNewTagName('')
      setShowNewTag(false)
      setSelectedTagIds(prev => [...prev, data.id])
      queryClient.invalidateQueries({ queryKey: ['tags'] })
      showToast('Tag created')
    },
    onError: (error) => {
      showToast(error instanceof Error ? error.message : 'Failed to create tag', 'error')
    },
  })

  // Ref to hold tag IDs to attach, so the mutation onSuccess always has the latest list
  const tagIdsToAttachRef = useRef<string[]>([])

  // Create link mutation
  const createLinkMutation = useMutation({
    mutationFn: (data: { url: string; alias?: string }) =>
      apiRequest<CreateLinkResponse>('/api/shorten', 'POST', data),
    onSuccess: async (data) => {
      queryClient.invalidateQueries({ queryKey: ['urls'] })
      queryClient.invalidateQueries({ queryKey: ['overview'] })
      setShortUrl(data.shortUrl)
      setOriginalUrl(url)
      // Attach tags from the ref (set right before mutate is called)
      const tagIds = tagIdsToAttachRef.current
      for (const tagId of tagIds) {
        try {
          await apiRequest(`/api/urls/${data.id}/tags/${tagId}`, 'POST')
        } catch {
          showToast('Failed to attach tag', 'error')
        }
      }
      if (tagIds.length > 0) {
        queryClient.invalidateQueries({ queryKey: ['url-tags', data.id] })
      }
      setShowSuccess(true)
    },
    onError: (error) => {
      showToast(error instanceof Error ? error.message : 'Failed to create link', 'error')
    }
  })

  const toggleTag = (tagId: string) => {
    setSelectedTagIds(prev =>
      prev.includes(tagId) ? prev.filter(id => id !== tagId) : [...prev, tagId]
    )
  }

  const handleSubmit = useCallback(async () => {
    if (!url.trim()) {
      showToast('Please enter a URL', 'error')
      return
    }

    // Collect all tag IDs to attach, including any pending new tag
    const finalTagIds = [...selectedTagIds]

    if (newTagName.trim() && showNewTag) {
      try {
        const tag = await apiRequest<{ id: string; name: string; color: string }>('/api/tags', 'POST', { name: newTagName, color: newTagColor })
        finalTagIds.push(tag.id)
        setNewTagName('')
        setShowNewTag(false)
        queryClient.invalidateQueries({ queryKey: ['tags'] })
      } catch (err) {
        showToast(err instanceof Error ? err.message : 'Failed to create tag', 'error')
        return
      }
    }

    // Store in ref so onSuccess can read it
    tagIdsToAttachRef.current = finalTagIds
    createLinkMutation.mutate({ url, alias: alias || undefined })
  }, [url, alias, newTagName, newTagColor, showNewTag, selectedTagIds, createLinkMutation, showToast, queryClient])

  const handleClose = useCallback(() => {
    setUrl('')
    setAlias('')
    setShortUrl('')
    setOriginalUrl('')
    setShowSuccess(false)
    setSelectedTagIds([])
    setShowNewTag(false)
    setNewTagName('')
    onClose()
  }, [onClose])

  // Handle keyboard shortcut (Cmd/Ctrl + Enter)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (isOpen && (e.metaKey || e.ctrlKey) && e.key === 'Enter') {
        e.preventDefault()
        handleSubmit()
      }
      if (isOpen && e.key === 'Escape') {
        handleClose()
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, handleSubmit, handleClose])

  const handleCreateAnother = () => {
    setUrl('')
    setAlias('')
    setShortUrl('')
    setOriginalUrl('')
    setShowSuccess(false)
    setSelectedTagIds([])
    setShowNewTag(false)
    setNewTagName('')
  }

  const copyToClipboard = async () => {
    await navigator.clipboard.writeText(shortUrl)
    showToast('Link copied to clipboard!')
  }

  // Generate preview alias
  const previewAlias = alias || 'your-link'
  const domain = typeof window !== 'undefined' ? getBaseUrl().replace(/^https?:\/\//, '') : 'urlzippy.com'

  if (!isOpen) return null

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center pt-[15vh] bg-black/5 dark:bg-black/50 backdrop-blur-[4px] px-4"
      onClick={handleClose}
    >
      <div
        className="w-full max-w-[640px] bg-white dark:bg-[#18181b] rounded-2xl shadow-2xl border border-gray-200/60 dark:border-white/10 overflow-hidden flex flex-col animate-slideUp"
        onClick={e => e.stopPropagation()}
      >
        {!showSuccess ? (
          <>
            {/* URL Input Header */}
            <div className="p-6 pb-4">
              <div className="relative group">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <span className="material-symbols-outlined text-gray-400 group-focus-within:text-[#1754cf] transition-colors">
                    link
                  </span>
                </div>
                <input
                  autoFocus
                  value={url}
                  onChange={e => setUrl(e.target.value)}
                  className="block w-full pl-12 pr-4 py-4 bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-lg text-lg focus:ring-2 focus:ring-[#1754cf]/20 focus:border-[#1754cf] transition-all placeholder:text-gray-400 dark:text-white"
                  placeholder="Paste long URL"
                  type="text"
                />
              </div>
            </div>

            {/* Form Content */}
            <div className="p-6 space-y-8">
              {/* Live Preview Card with Gradient */}
              <div className="bg-gradient-to-br from-[#1754cf] to-[#4e81ee] rounded-xl p-8 flex flex-col items-center justify-center text-center shadow-inner relative overflow-hidden">
                {/* Grid pattern overlay */}
                <div className="absolute inset-0 opacity-10 pointer-events-none">
                  <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
                    <defs>
                      <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
                        <path d="M 40 0 L 0 0 0 40" fill="none" stroke="white" strokeWidth="1" />
                      </pattern>
                    </defs>
                    <rect width="100%" height="100%" fill="url(#grid)" />
                  </svg>
                </div>

                <p className="text-white/80 text-sm font-medium uppercase tracking-widest mb-2 z-10">Live Preview</p>
                <h2 className="text-white text-3xl md:text-4xl font-bold tracking-tight z-10">
                  <span>{domain}/</span>
                  <span className="text-white/90">{previewAlias}</span>
                </h2>
              </div>

              {/* Custom Alias & Tags Row */}
              <div className="grid grid-cols-2 gap-8">
                <div className="space-y-3">
                  <label className="text-xs font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider">
                    Custom Alias
                  </label>
                  <input
                    value={alias}
                    onChange={e => setAlias(e.target.value)}
                    className="w-full bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 rounded-lg h-11 px-4 text-sm font-medium focus:border-[#1754cf] focus:ring-2 focus:ring-[#1754cf]/20 transition-all dark:text-white"
                    placeholder="my-custom-alias"
                    type="text"
                  />
                </div>
                <div className="space-y-3">
                  <label className="text-xs font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider">
                    Quick Tags
                  </label>
                  <div className="flex flex-wrap gap-2 items-start">
                    {allTags.map(tag => {
                      const isSelected = selectedTagIds.includes(tag.id)
                      return (
                        <button
                          key={tag.id}
                          type="button"
                          onClick={() => toggleTag(tag.id)}
                          className={`px-3 py-1.5 rounded-full border text-xs font-semibold transition-colors flex items-center gap-1.5 ${isSelected
                              ? 'border-transparent text-white'
                              : 'border-gray-200 dark:border-white/10 bg-white dark:bg-white/5 text-gray-700 dark:text-gray-300 hover:border-[#1754cf]'
                            }`}
                          style={isSelected ? { backgroundColor: tag.color } : {}}
                        >
                          <span className="size-2 rounded-full" style={{ backgroundColor: isSelected ? '#fff' : tag.color }} />
                          {tag.name}
                        </button>
                      )
                    })}
                    {showNewTag ? (
                      <div className="flex items-center bg-white dark:bg-white/5 border-2 border-[#0d121b] dark:border-white rounded-lg p-1 shadow-sm">
                        <input
                          autoFocus
                          type="text"
                          value={newTagName}
                          onChange={e => setNewTagName(e.target.value)}
                          placeholder="Tag name..."
                          maxLength={50}
                          className="w-24 bg-transparent border-none p-1 text-xs focus:ring-0 placeholder:text-gray-300 dark:placeholder:text-gray-600 font-medium text-[#0d121b] dark:text-white"
                          onKeyDown={e => {
                            if (e.key === 'Enter' && newTagName.trim()) { e.preventDefault(); createTagMutation.mutate() }
                            if (e.key === 'Escape') { setShowNewTag(false); setNewTagName('') }
                          }}
                        />
                        <div className="w-px h-4 bg-gray-200 dark:bg-white/10 mx-1" />
                        <div className="flex items-center gap-1 px-1">
                          {QUICK_TAG_COLORS.map(c => (
                            <button
                              key={c}
                              type="button"
                              onClick={() => setNewTagColor(c)}
                              className={`rounded-full border transition-transform ${newTagColor === c ? 'w-5 h-5 ring-2 ring-offset-2 dark:ring-offset-[#18181b]' : 'w-4 h-4 border-gray-100 dark:border-white/10 hover:scale-110'}`}
                              style={{ backgroundColor: c, ...(newTagColor === c ? { ringColor: c } : {}) }}
                            />
                          ))}
                        </div>
                        <div className="w-px h-4 bg-gray-200 dark:bg-white/10 mx-1" />
                        <button
                          type="button"
                          onClick={() => newTagName.trim() && createTagMutation.mutate()}
                          disabled={!newTagName.trim() || createTagMutation.isPending}
                          className="p-1 text-[#0d121b] dark:text-white hover:bg-gray-100 dark:hover:bg-white/10 rounded-md disabled:opacity-50"
                        >
                          <span className="material-symbols-outlined text-[18px]">check</span>
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setShowNewTag(true)}
                        className="px-3 py-1.5 rounded-full border border-dashed border-gray-300 dark:border-white/20 text-xs font-semibold text-gray-500 dark:text-gray-400 hover:border-[#1754cf] hover:text-[#1754cf] transition-colors flex items-center gap-1"
                      >
                        <span className="material-symbols-outlined text-[14px]">add</span>
                        New Tag
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Footer Actions */}
            <div className="px-6 py-6 bg-gray-50 dark:bg-white/5 border-t border-gray-100 dark:border-white/5 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-1.5 text-xs text-gray-400 font-medium">
                  <kbd className="px-1.5 py-0.5 bg-white dark:bg-white/10 border border-gray-200 dark:border-white/10 rounded-md shadow-sm">
                    ⌘
                  </kbd>
                  <kbd className="px-1.5 py-0.5 bg-white dark:bg-white/10 border border-gray-200 dark:border-white/10 rounded-md shadow-sm">
                    Enter
                  </kbd>
                  <span className="ml-1 uppercase tracking-tight text-[10px]">to Create</span>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={handleClose}
                  className="h-10 px-5 text-sm font-semibold text-gray-500 hover:bg-gray-100 dark:hover:bg-white/10 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={createLinkMutation.isPending}
                  className="h-10 px-5 text-sm font-bold text-white bg-[#0d121b] dark:bg-white dark:text-[#0d121b] hover:opacity-90 rounded-lg shadow-sm transition-all disabled:opacity-50 flex items-center gap-2"
                >
                  {createLinkMutation.isPending ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white dark:border-[#0d121b] border-t-transparent rounded-full animate-spin" />
                      Creating...
                    </>
                  ) : (
                    <>
                      Create Link
                      <span className="material-symbols-outlined text-[16px]">arrow_forward</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </>
        ) : (
          /* Success View */
          <>
            <div className="p-8 flex flex-col items-center animate-slideUp">
              {/* Success Icon & Title */}
              <div className="flex flex-col items-center mb-8">
                <div className="w-16 h-16 bg-green-100 dark:bg-green-900/20 rounded-full flex items-center justify-center mb-4">
                  <span className="material-symbols-outlined text-green-600 dark:text-green-400 text-4xl">
                    check_circle
                  </span>
                </div>
                <h2 className="text-[#0e121b] dark:text-white text-2xl font-bold tracking-tight text-center">
                  Link created successfully!
                </h2>
              </div>

              {/* Success URL Card */}
              <div className="w-full bg-[#f8f9fc] dark:bg-[#1e2638] border border-[#e7ebf3] dark:border-[#2d364d] rounded-xl p-6 mb-6">
                <div className="flex flex-col gap-4">
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex flex-col overflow-hidden flex-1">
                      <span className="text-xs font-semibold text-[#1754cf] uppercase tracking-wider mb-1">
                        Your short link
                      </span>
                      <input
                        type="text"
                        value={shortUrl}
                        readOnly
                        onClick={e => (e.target as HTMLInputElement).select()}
                        className="text-[#0e121b] dark:text-white text-xl font-bold leading-tight truncate bg-transparent border-none focus:ring-0 cursor-text select-all p-0"
                      />
                    </div>
                    <button
                      onClick={copyToClipboard}
                      className="flex min-w-[100px] cursor-pointer items-center justify-center gap-2 rounded-lg h-10 px-4 bg-[#1754cf] text-white text-sm font-bold hover:bg-[#1754cf]/90 transition-colors"
                    >
                      <span className="material-symbols-outlined text-sm">content_copy</span>
                      <span>Copy</span>
                    </button>
                  </div>

                  <div className="h-px bg-[#e7ebf3] dark:bg-[#2d364d] w-full" />

                  <div className="flex flex-col gap-3">
                    <div>
                      <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider block mb-1">
                        Destination
                      </span>
                      <p className="text-[#4e6797] dark:text-gray-300 text-sm font-normal truncate">
                        {originalUrl}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-3 w-full">
                <button
                  onClick={handleCreateAnother}
                  className="flex-1 flex cursor-pointer items-center justify-center rounded-lg h-12 px-6 border-2 border-[#1754cf] text-[#1754cf] text-sm font-bold hover:bg-[#1754cf]/5 transition-colors"
                >
                  <span className="material-symbols-outlined mr-2">add_link</span>
                  Create Another
                </button>
                <button
                  onClick={handleClose}
                  className="flex-1 flex cursor-pointer items-center justify-center rounded-lg h-12 px-6 bg-transparent text-gray-500 dark:text-gray-400 text-sm font-bold hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                >
                  Done
                </button>
              </div>
            </div>

            {/* Footer Info */}
            <div className="px-8 py-4 bg-gray-50 dark:bg-[#161c2b] border-t border-[#e7ebf3] dark:border-[#2d364d] flex justify-center">
              <p className="text-xs text-gray-400 dark:text-gray-500 flex items-center gap-1">
                <span className="material-symbols-outlined text-[14px]">bolt</span>
                Automatically tracked in your dashboard
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
